import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import {
  BuildingOfficeIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  TruckIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ClockIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { monthTranslations } from '../utils/monthTranslations';

const SuperAdminDashboard = () => {
  const { t, i18n } = useTranslation();
  const { getCompanies } = useAuth();
  const [revenueData, setRevenueData] = useState([]);
  const [companiesData, setCompaniesData] = useState([]);
  const [planDistribution, setPlanDistribution] = useState([]);

  const companies = getCompanies();

  useEffect(() => {

    // Static mock revenue data for the last 12 months with proper translations
    const staticRevenueData = [
      { month: monthTranslations[i18n.language]?.jan || 'Jan', revenue: 8500, companies: 18, growth: 5 },
      { month: monthTranslations[i18n.language]?.feb || 'Feb', revenue: 9200, companies: 20, growth: 8 },
      { month: monthTranslations[i18n.language]?.mar || 'Mar', revenue: 9800, companies: 22, growth: 6 },
      { month: monthTranslations[i18n.language]?.apr || 'Apr', revenue: 10500, companies: 25, growth: 12 },
      { month: monthTranslations[i18n.language]?.may || 'May', revenue: 11200, companies: 27, growth: 7 },
      { month: monthTranslations[i18n.language]?.jun || 'Jun', revenue: 12100, companies: 30, growth: 11 },
      { month: monthTranslations[i18n.language]?.jul || 'Jul', revenue: 12800, companies: 32, growth: 6 },
      { month: monthTranslations[i18n.language]?.aug || 'Aug', revenue: 13600, companies: 35, growth: 9 },
      { month: monthTranslations[i18n.language]?.sep || 'Sep', revenue: 14200, companies: 37, growth: 4 },
      { month: monthTranslations[i18n.language]?.oct || 'Oct', revenue: 15100, companies: 40, growth: 13 },
      { month: monthTranslations[i18n.language]?.nov || 'Nov', revenue: 15800, companies: 42, growth: 5 },
      { month: monthTranslations[i18n.language]?.dec || 'Dec', revenue: 16500, companies: 45, growth: 8 },
    ];

    const staticCompaniesGrowth = [
      { month: monthTranslations[i18n.language]?.jan || 'Jan', active: 18, trial: 5, churned: 2 },
      { month: monthTranslations[i18n.language]?.feb || 'Feb', active: 20, trial: 4, churned: 1 },
      { month: monthTranslations[i18n.language]?.mar || 'Mar', active: 22, trial: 6, churned: 2 },
      { month: monthTranslations[i18n.language]?.apr || 'Apr', active: 25, trial: 5, churned: 1 },
      { month: monthTranslations[i18n.language]?.may || 'May', active: 27, trial: 7, churned: 3 },
      { month: monthTranslations[i18n.language]?.jun || 'Jun', active: 30, trial: 4, churned: 1 },
      { month: monthTranslations[i18n.language]?.jul || 'Jul', active: 32, trial: 6, churned: 2 },
      { month: monthTranslations[i18n.language]?.aug || 'Aug', active: 35, trial: 5, churned: 1 },
      { month: monthTranslations[i18n.language]?.sep || 'Sep', active: 37, trial: 8, churned: 2 },
      { month: monthTranslations[i18n.language]?.oct || 'Oct', active: 40, trial: 6, churned: 3 },
      { month: monthTranslations[i18n.language]?.nov || 'Nov', active: 42, trial: 7, churned: 1 },
      { month: monthTranslations[i18n.language]?.dec || 'Dec', active: 45, trial: 5, churned: 2 },
    ];

    const planData = [
      { name: 'Trial', value: companies.filter(c => c.plan === 'trial').length, color: '#6B7280' },
      { name: 'Basic', value: companies.filter(c => c.plan === 'basic').length, color: '#3B82F6' },
      { name: 'Professional', value: companies.filter(c => c.plan === 'professional').length, color: '#10B981' },
      { name: 'Enterprise', value: companies.filter(c => c.plan === 'enterprise').length, color: '#8B5CF6' },
    ];

    setRevenueData(staticRevenueData);
    setCompaniesData(staticCompaniesGrowth);
    setPlanDistribution(planData);
  }, [companies, i18n.language]);

  const kpiData = [
    {
      title: 'Total Companies',
      value: companies.length,
      change: '+12%',
      trend: 'up',
      icon: BuildingOfficeIcon,
      color: 'blue',
    },
    {
      title: 'Monthly Revenue',
      value: `$${companies.reduce((sum, c) => sum + (c.status === 'active' && c.billing ? c.billing.amount : 0), 0).toLocaleString('en-US')}`,
      change: '+8.2%',
      trend: 'up',
      icon: CurrencyDollarIcon,
      color: 'green',
    },
    {
      title: 'Active Users',
      value: '147', // Static value instead of random
      change: '+15%',
      trend: 'up',
      icon: UserGroupIcon,
      color: 'purple',
    },
    {
      title: 'Total Shipments',
      value: '2,384', // Static value instead of random
      change: '+3.1%',
      trend: 'up',
      icon: TruckIcon,
      color: 'orange',
    },
  ];

  const recentActivities = [
    {
      id: 1,
      type: 'company_registered',
      title: 'New Company Registered',
      description: 'Global Shipping Inc signed up for Professional plan',
      time: '2 hours ago',
      icon: BuildingOfficeIcon,
      color: 'green',
    },
    {
      id: 2,
      type: 'plan_upgraded',
      title: 'Plan Upgraded',
      description: 'ABC Logistics upgraded from Basic to Professional',
      time: '5 hours ago',
      icon: ArrowUpIcon,
      color: 'blue',
    },
    {
      id: 3,
      type: 'payment_received',
      title: 'Payment Received',
      description: 'XYZ Transport Co paid $199.99 for Enterprise plan',
      time: '1 day ago',
      icon: CurrencyDollarIcon,
      color: 'green',
    },
    {
      id: 4,
      type: 'trial_expiring',
      title: 'Trial Expiring Soon',
      description: 'Fast Delivery Ltd trial expires in 3 days',
      time: '1 day ago',
      icon: ExclamationTriangleIcon,
      color: 'orange',
    },
    {
      id: 5,
      type: 'support_ticket',
      title: 'Support Ticket',
      description: 'Regional Express requested integration help',
      time: '2 days ago',
      icon: ClockIcon,
      color: 'gray',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('superadmin.system_overview')}</h1>
        <p className="mt-1 text-sm text-gray-500">
          {t('superadmin.monitor_platform')}
        </p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {kpiData.map((kpi, index) => {
          const Icon = kpi.icon;
          const colorClasses = {
            blue: 'bg-blue-50 text-blue-600',
            green: 'bg-green-50 text-green-600',
            purple: 'bg-purple-50 text-purple-600',
            orange: 'bg-orange-50 text-orange-600',
          };

          return (
            <div key={index} className="card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{kpi.title}</p>
                  <p className="text-2xl font-semibold text-gray-900 mt-1">{kpi.value}</p>
                  <div className="flex items-center mt-2">
                    {kpi.trend === 'up' ? (
                      <ArrowUpIcon className="h-4 w-4 text-green-500" />
                    ) : (
                      <ArrowDownIcon className="h-4 w-4 text-red-500" />
                    )}
                    <span className={`text-sm ml-1 ${
                      kpi.trend === 'up' ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {kpi.change}
                    </span>
                    <span className="text-sm text-gray-500 ml-1">vs last month</span>
                  </div>
                </div>
                <div className={`p-3 rounded-lg ${colorClasses[kpi.color]}`}>
                  <Icon className="h-6 w-6" />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Chart */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Monthly Revenue</h3>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-500">Last 12 months</span>
            </div>
          </div>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={revenueData}>
                <defs>
                  <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="month" 
                  stroke="#6B7280" 
                  tick={{ 
                    fontSize: 12,
                    fontFamily: i18n.language === 'ar' ? 'Amiri, Arial, sans-serif' : 'Arial, sans-serif',
                    textAnchor: i18n.language === 'ar' ? 'end' : 'middle'
                  }}
                />
                <YAxis stroke="#6B7280" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="revenue"
                  stroke="#10B981"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorRevenue)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Plan Distribution */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Plan Distribution</h3>
          </div>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={planDistribution}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {planDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="grid grid-cols-2 gap-4 mt-4">
            {planDistribution.map((plan, index) => (
              <div key={index} className="flex items-center space-x-2">
                <div 
                  className="w-3 h-3 rounded-full" 
                  style={{ backgroundColor: plan.color }}
                />
                <span className="text-sm text-gray-600">{plan.name}</span>
                <span className="text-sm font-medium text-gray-900">({plan.value})</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Company Growth */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Company Growth</h3>
          </div>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={companiesData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="month" 
                  stroke="#6B7280" 
                  tick={{ 
                    fontSize: 12,
                    fontFamily: i18n.language === 'ar' ? 'Amiri, Arial, sans-serif' : 'Arial, sans-serif',
                    textAnchor: i18n.language === 'ar' ? 'end' : 'middle'
                  }}
                />
                <YAxis stroke="#6B7280" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Bar dataKey="active" stackId="a" fill="#10B981" name={t('common.active')} />
                <Bar dataKey="trial" stackId="a" fill="#F59E0B" name="Trial" />
                <Bar dataKey="churned" stackId="a" fill="#EF4444" name="Churned" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Recent Activities */}
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Activities</h3>
          <div className="space-y-4">
            {recentActivities.map((activity) => {
              const Icon = activity.icon;
              const colorClasses = {
                green: 'bg-green-100 text-green-600',
                blue: 'bg-blue-100 text-blue-600',
                orange: 'bg-orange-100 text-orange-600',
                gray: 'bg-gray-100 text-gray-600',
              };

              return (
                <div key={activity.id} className="flex items-start space-x-3">
                  <div className={`p-2 rounded-lg ${colorClasses[activity.color]}`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">{activity.title}</p>
                    <p className="text-sm text-gray-500">{activity.description}</p>
                    <p className="text-xs text-gray-400 mt-1">{activity.time}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminDashboard; 